import { InputAdornment } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import { notificationSettingsKeys } from "../../../../../../api/queryKeys";
import { operationStatus } from "../../../../../../components/ActionStatus";
import { additionalTimezone, customerTimezone } from "../../../../constants";
import { field_IDs_mapping } from "../../../fieldMapping";

const notificationTimezone = {
  ...customerTimezone,
  ...additionalTimezone,
};

function getGMTOffset(ianaTimezone) {
  try {
    const date = new Date();
    const options = { timeZone: ianaTimezone, timeZoneName: "longOffset" };
    const gmtOffsetString = date
      .toLocaleString("en-US", options)
      .match(/GMT([+-]\d{2}:\d{2})/);

    if (gmtOffsetString) {
      return gmtOffsetString[0];
    }

    return "Invalid timezone";
  } catch (error) {
    return "Invalid timezone";
  }
}

export function createTimezoneTitle(ianaTimezone) {
  const gmt = getGMTOffset(ianaTimezone);
  let gmtText = "";
  if (gmt !== "Invalid timezone") gmtText = `(${gmt})`;
  if (notificationTimezone[ianaTimezone].length === 1)
    return `${notificationTimezone[ianaTimezone][0]} ${gmtText}`;
  else
    return `${notificationTimezone[ianaTimezone][0]} ${gmtText} ${notificationTimezone[ianaTimezone][1]}`;
}

const timezoneTitles = Object.keys(notificationTimezone).reduce((acc, key) => {
  acc[key] = createTimezoneTitle(key);
  return acc;
}, {});

export default function TimezoneSelector({
  method,
  setFetchStatus,
  selectedTimezone,
  disabled,
}) {
  const colors = useColors();
  const { updateNotificationSetting } = useAPI();
  const [autocompleteDisabled, setAutocompleteDisabled] = useState(false);
  const queryClient = useQueryClient();

  const onTimezoneMenuClose = (newTimezone) => {
    if (newTimezone === selectedTimezone) return;

    setAutocompleteDisabled(true);
    setFetchStatus(operationStatus.LOADING);

    updateNotificationSetting(
      method,
      field_IDs_mapping.FREQUENCY.TIMEZONE,
      newTimezone
    )
      .then(() => {
        setFetchStatus(operationStatus.SUCCESS);
        queryClient.invalidateQueries({
          queryKey: notificationSettingsKeys.all,
        });
      })
      .catch(() => {
        setFetchStatus(operationStatus.ERROR);
      })
      .finally(() => {
        setAutocompleteDisabled(false);
      });
  };

  return [
    <Autocomplete
      value={selectedTimezone}
      options={Object.keys(timezoneTitles)}
      getOptionLabel={(option) => timezoneTitles[option]}
      onChange={(event, newValue) => {
        onTimezoneMenuClose(newValue);
      }}
      disabled={autocompleteDisabled || disabled}
      sx={{
        color: colors.text_primary,
        borderRadius: "10px",
        "& .MuiAutocomplete-clearIndicator": {
          display: "none",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            inputProps: {
              ...params.inputProps,
              style: {
                paddingRight: "0px", // Adjusted to give more space before the arrow icon
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              height: "32px",
              width: "285px",
              "& fieldset": {
                borderColor: colors.divider2,
              },
              "&:hover fieldset": {
                borderColor: colors.divider2,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.divider2,
              },
              "&.Mui-disabled fieldset": {
                borderColor: colors.divider2,
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "12px",
            },
          }}
        />
      )}
      ListboxProps={{
        sx: {
          "& .MuiAutocomplete-option": {
            fontSize: "12px",
          },
        },
      }}
      componentsProps={{
        popupIndicator: {
          disableRipple: true,
        },
      }}
    />,
  ];
}
